/**
 * Utility for resolving crypto and network icons using Vite's asset resolution.
 * Uses import.meta.glob with eager loading to ensure assets are included in production builds.
 */

// Eager import all crypto assets - Vite will bundle these at build time
const cryptoAssets = import.meta.glob<{ default: string }>(
    '../../assets/images/crypto/*.{svg,png}',
    { eager: true }
)

// Helper to extract filename from path
function getFilename(path: string): string {
    return path.split('/').pop() || ''
}

// Build lookup maps from glob imports
const assetsByFilename: Record<string, string> = {}
for (const [path, module] of Object.entries(cryptoAssets)) {
    const filename = getFilename(path)
    assetsByFilename[filename] = module.default
}

// Crypto symbol to filename mapping
const CRYPTO_ICON_FILES: Record<string, string> = {
    BTC: 'btc-coin.svg',
    USDT: 'usdt.svg',
    USDC: 'usdc.svg',
    ETH: 'eth.svg',
    BNB: 'bnb-coin.svg',
    BUSD: 'busd.svg',
    SOL: 'sol.svg',
    TON: 'ton.svg',
    TRX: 'trx.svg',
    LTC: 'ltc.svg',
    BCH: 'bch.svg',
    HMC: 'hmc.svg',
    GNK: 'gnk.svg',
    POL: 'pol.svg',
    FANTOM: 'fantom.svg',
    USD: 'usd.svg',
}

// Network ID to filename mapping
const NETWORK_ICON_FILES: Record<string, string> = {
    bitcoin: 'btc.svg',
    // With hyphens (legacy format)
    'erc-20': 'eth.svg',
    'bep-20': 'bnb.svg',
    'trc-20': 'trx.svg',
    // Without hyphens (new format used in DepositModal)
    erc20: 'eth.svg',
    bep20: 'bnb.svg',
    trc20: 'trx.svg',
    arb: 'eth.svg',
    sol: 'sol.svg',
    solana: 'sol.svg',  // alias for 'sol' used in cryptoConfig
    ton: 'ton.svg',
    gonka: 'gnk.svg',
    hmc: 'hmc.svg',
    pol: 'pol.svg',
    fantom: 'fantom.svg',
}

/**
 * Returns the resolved URL for a crypto icon.
 * @param symbol - The currency symbol (e.g., 'BTC', 'USDT')
 */
export function getCryptoIcon(symbol: string): string {
    const filename = CRYPTO_ICON_FILES[symbol.toUpperCase()]
    if (!filename) return ''
    return assetsByFilename[filename] || ''
}

/**
 * Returns the resolved URL for a network icon.
 * @param networkId - The network identifier (e.g., 'bitcoin', 'erc-20')
 */
export function getNetworkIcon(networkId: string): string {
    const filename = NETWORK_ICON_FILES[networkId.toLowerCase()] || 'eth.svg'
    return assetsByFilename[filename] || ''
}

/**
 * Checks if a crypto icon exists for the given symbol.
 * @param symbol - The currency symbol
 */
export function hasCryptoIcon(symbol: string): boolean {
    return symbol.toUpperCase() in CRYPTO_ICON_FILES
}

/**
 * Оборачивает URL иконки в CSS-функцию `url()` для подстановки в кастомное
 * свойство (`--tile-icon`, `--nbell-icon`), которое читает `mask`.
 *
 * Кавычки здесь обязательны. В проде Vite инлайнит мелкие SVG (< 4 КБ) в
 * data-URI и при этом заменяет внутри двойные кавычки на ОДИНАРНЫЕ:
 * `data:image/svg+xml,%3csvg%20xmlns='http://www.w3.org/2000/svg'...`.
 * Некавыченный `url(...)` одинарную кавычку не допускает — по спецификации
 * это bad-url-token, значение свойства становится невалидным, `mask`
 * откатывается к `none` и элемент заливается своим `background-color`
 * целиком: вместо иконки сплошной жёлтый квадрат. В dev тот же код работает,
 * потому что там подставляется обычный путь без кавычек — поэтому баг и виден
 * только в собранном приложении.
 *
 * Двойные кавычки безопасны: Vite их из data-URI как раз и вычищает.
 */
export function cssIconUrl(src: string): string {
    return `url("${src}")`
}

/**
 * Returns the optimized preview URL for a miner image if available.
 * @param originalUrl - The original PNG URL (e.g. miner.png)
 * @returns Optimized URL (e.g. miner_preview.jpg)
 */
export function getMinerPreviewUrl(originalUrl: string | undefined): string {
    if (!originalUrl) return '';
    // Only transform if it's a PNG and not already a preview
    if (originalUrl.toLowerCase().endsWith('.png') && !originalUrl.includes('_preview')) {
        return originalUrl.replace(/\.png$/i, '_preview.jpg');
    }
    return originalUrl;
}
